<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-blue">
          <graph-tool-bar :showSource="false" granularity="day" @on-selected="onPanelSelect"></graph-tool-bar>
        </md-card-header>

        <md-card-content>
          <highcharts class="charts" :options="chartOptions" ref="vectorChart"></highcharts>
        </md-card-content>
      </md-card>
    </div>

    <right-bar-settings
      v-if="permissions >= 3"
      :permissions="permissions"
      @onUpdate="onUpdateChartSettings"
    ></right-bar-settings>
    <snack-bar ref="snackbar"></snack-bar>
  </div>
</template>


<script>
import graphToolBar from "@/components/GraphToolBar";
import areaChartOptions from "@/config/charts/area";
import axios from "axios";
import { getSelectedDeviceToken } from "@/storage/memoryStorage";
import snackBar from "@/components/SnackBar";
import RightBarSettings from "./RightBarSettings";

export default {
  components: {
    "graph-tool-bar": graphToolBar,
    "snack-bar": snackBar,
    RightBarSettings
  },

  data() {
    return {
      chartOptions: null,
      permissions: 4
    };
  },

  computed: {
    WSmessage() {
      return this.$store.getters.getDataMessage;
    }
  },

  watch: {
    WSmessage: function(payload, oldPayload) {
      if (payload) {
        if (payload.F_U) {
          this.updateChartData(0, payload.F_U.L1, false, payload.U.L1, "V");
          this.updateChartData(2, payload.F_U.L2, false, payload.U.L2, "V");
          this.updateChartData(4, payload.F_U.L3, false, payload.U.L3, "V");
        } else {
          this.$refs.snackbar.notify(
            this.$lang.messages.api_communication_error
          );
        }

        if (payload.F_I) {
          this.updateChartData(1, payload.F_U.L1 + payload.F_I.L1, false, payload.I.L1, "A");
          this.updateChartData(3, payload.F_U.L2 + payload.F_I.L2, false, payload.I.L2, "A");
          this.updateChartData(5, payload.F_U.L3 + payload.F_I.L3, true, payload.I.L3, "A");
        } else {
          this.$refs.snackbar.notify(
            this.$lang.messages.api_communication_error
          );
        }
      } else {
        this.$refs.snackbar.notify(this.$lang.messages.api_communication_error);
      }
    }
  },

  created() {
    this.chartOptions = {
      chart: {
        polar: true
      },

      title: {
        text: null
      },

      pane: {
        startAngle: 0,
        endAngle: 360
      },

      xAxis: {
        tickInterval: 45,
        min: 0,
        max: 360,
        labels: {
          formatter: function() {
            return this.value + "°";
          }
        },
        reversed: false,
        plotLines: [
          {
            color: "#BFDDF9",
            width: 2,
            value: [0, 2],
            zIndex: 1
          }
        ]
      },

      yAxis: {
        type: "logarithmic",
        min: 0.1,
        max: 300,
        //tickInterval: 5,
        labels: {
          formatter: function() {
            return this.value >= 0 ? this.value : null;
          }
        }
      },

      tooltip: {
        pointFormat:
          "<span style='color:{point.color}; font-size: 14px'>●</span> {series.name} <br/><span style='font-size: 13px'>Angle <b>{point.x}°</b><br/>Value <b>{point.y:.1f} {point.unit}</b></span>",
        headerFormat: ""
      },

      plotOptions: {
        series: {
          pointStart: 0,
          pointInterval: 45
        },
        column: {
          pointPadding: 0,
          groupPadding: 0
        },
        line: {
          //lineWidth: 0
        }
      },

      series: [],
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: [
              "viewFullscreen",
              "printChart",
              "separator",
              "downloadCSV",
              "downloadXLS",
              "downloadPNG",
              "downloadPDF",
              "viewData"
            ]
          }
        }
      },
      colors: [
        "#00bcd4",
        "#f44336",
        "#4caf50",
        "#ff9800",
        "#fb4579",
        "#9761c8",
        "#4b63ec",
        "#fb636a",
        "#206692",
        "#668891"
      ],

      credits: {
        enabled: false
      },
      legend: {
        enabled: false,
        verticalAlign: "top"
      }
    };

    this.chartOptions.title.text = "Vector diagram";
  },

  mounted() {
    this.setChartSeries("L1 Voltage", 0);
    this.setChartSeries("L1 Current", 0, true);
    this.setChartSeries("L2 Voltage", 0);
    this.setChartSeries("L2 Current", 0, true);
    this.setChartSeries("L3 Voltage", 0);
    this.setChartSeries("L3 Current", 0, true);

    const { chart } = this.$refs.vectorChart;
    let chartPermissionsList = {};

    if (this.permissions >= 1) {
      chartPermissionsList.legend = {
        enabled: true
      };
    }

    if (this.permissions >= 3) {
      chartPermissionsList.xAxis = {
        reversed: this.clockWiseDirection === "true" ? false : true
      };
    }

    if (this.permissions >= 4) {
      chartPermissionsList.pane = {
        startAngle: Number(this.zeroPosition),
        endAngle: Number(this.zeroPosition) + 360
      };
    }

    chart.update(chartPermissionsList);
  },

  methods: {
    onPanelSelect(data) {},

    onUpdateChartSettings(data) {
      const { chart } = this.$refs.vectorChart;

      chart.update({
        xAxis: {
          reversed: data.clockWiseDirection
        },
        pane: {
          startAngle: data.zeroPosition,
          endAngle: data.zeroPosition + 360
        }
      });
    },

    setChartSeries(name, value, dash = false) {
      const { chart } = this.$refs.vectorChart;

      chart.addSeries({
        name: name,
        type: "scatter",
        lineWidth: 2,
        dashStyle: dash === true ? "Dash" : "Solid",
        data: [
          {
            x: 0.1,
            y: 0.1,
            marker: {
              enabled: false
            }
          },
          {
            x: Number(value),
            y: 0,
            marker: {
              enabled: false
            }
          }
        ]
      });
    },

    updateChartData(seriesIndex, value, redraw = false, ylength = 0, unit = "") {
      const { chart } = this.$refs.vectorChart;

      //chart.series[seriesIndex].removePoint(0, false, false);
      chart.series[seriesIndex].removePoint(1, false, false);

      chart.series[seriesIndex].addPoint(
        {
          x: value,
          y: ylength,
          unit: unit,
          marker: {
            enabled: false
          }
          //color: "#00FF00"
        },
        false,
        false,
        false,
        false
      );

      if (redraw) {
        chart.redraw();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.md-layout {
  margin-top: -29px;
}
</style>